import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  TablePagination,
} from "@mui/material";
import { PiSortDescendingLight, PiSortAscendingLight } from "react-icons/pi";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { FaEdit, FaTrash, FaAddressBook, FaBriefcase } from "react-icons/fa";
import authService from "../../../utils/authService";
import { clearAdminCompanyListCache } from "../../../utils/adminCompanyCache";


interface Data {
  company_id?: string;
  company_name: string;
  industry?: string | null;
  contact_number?: string | null;
  country?: string | null;
  state?: string | null;
  city?: string | null;
  del_row?: boolean;
  job_profile_open?: number;
  job_profile_closed?: number;
  job_profile_in_progress?: number;
}

const emptyForm = (): Data => ({
  company_id: "",
  company_name: "",
  industry: "",
  contact_number: "",
  country: "",
  state: "",
  city: "",
});

const Company: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isPlacementOffice = location.pathname.startsWith("/placement-officer");
  const [companies, setCompanies] = useState<Data[]>([]);

  // Helper function to check if a company is deleted (handles both boolean and string)
  const isDeleted = (company: Data): boolean => {
    return company.del_row === true ;
  };  
  
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string>("new");
  const [loading, setLoading] = useState<boolean>(false);
  
  const [formData, setFormData] = useState<Data>(emptyForm());
  const [error, setError] = useState<string>("");
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [selectedCompany, setSelectedCompany] = useState<Data>()
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  /** API pagination is 1-based */
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [industries, setIndustries] = useState<string[]>([]);

  const fetchIndustries = async () => {
    try {
      const response = await authService.get("admin/company/get-all-industry");
      setIndustries(response.data.industry || []);
    } catch (error) {
      console.error("Error fetching industries:", error);
      setIndustries([]);
    }
  };

  useEffect(() => {
    fetchIndustries();
  }, []);

  const handleModal = (type: string, company?: Data): void => {
    setError("")
    setModalType(type);
    if (type === "new") {
      setFormData(emptyForm());
    } else if (company) {
      setFormData(company);
    }
    setShowModal(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    if (name === "company_name") {
      setFormData((prevData) => ({ ...prevData, [name]: value.replace(/[^a-zA-Z\s]/g, "") }));
      return;
    }
    if (name === "contact_number") {
      const digits = value.replace(/\D/g, "").slice(0, 10);
      setFormData((prevData) => ({ ...prevData, [name]: digits }));
      return;
    }
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSave = async (): Promise<void> => {
    setError("");
    const existingCompany = companies.find(company => (
      company.company_name.toLowerCase() === formData.company_name.toLowerCase() && 
      company.company_id?.toLowerCase() !== formData.company_id?.toLowerCase()
    ));
  
    if (existingCompany) {
      setError("Company Name not available");
      return;
    }
    if (formData.company_name=='') {
      setError("Please enter a company name");
      return;
    }
    if (!/^[a-zA-Z\s]+$/.test(formData.company_name.trim())) {
      setError("Company name must contain only letters and spaces");
      return;
    }
    if (!(formData.industry ?? "").trim()) {
      setError("Please select an industry");
      return;
    }
    const contactNum = (formData.contact_number ?? "").trim();
    if (contactNum && contactNum.replace(/\D/g, "").length !== 10) {
      setError("Contact number must have exactly 10 digits");
      return;
    }

    const companyToSend = {
      company_id: formData.company_id || "",
      company_name: formData.company_name,
      industry: formData.industry || null,
      contact_number: formData.contact_number || null,
      country: formData.country || null,
      state: formData.state || null,
      city: formData.city || null,
    };

    try {
      setLoading(true);
      await authService.post("admin/company/create-company", companyToSend);
      clearAdminCompanyListCache();
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Error saving company:', error);
      return;
    }

    setShowModal(false);
    fetchCompanies();
  };

  useEffect(() => {
    fetchCompanies();
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (total <= 0) return;
    const maxPage = Math.max(1, Math.ceil(total / rowsPerPage));
    if (page > maxPage) setPage(maxPage);
  }, [total, rowsPerPage, page]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setCompanies([]);
      const params = new URLSearchParams({ page: String(page), page_size: String(rowsPerPage) });
      const response = await authService.get(`admin/company/?${params.toString()}`);
      const data = response.data;
      setCompanies(data.companies || []);
      setTotal(data.total ?? 0);
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (company: Data): void => {
    setSelectedCompany(company);
    setShowDeleteModal(true);
  };
  
  const confirmDelete = async (): Promise<void> => {
    if (selectedCompany?.company_id) {
      try {
        await authService.delete("admin/company/", {
          company_id: selectedCompany.company_id,
        });
        clearAdminCompanyListCache();
        fetchCompanies();
        setShowDeleteModal(false);
      } catch (error) {
        console.error("Error deleting company:", error);
      }
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedCompanies = (): Data[] => {
    const filtered = companies
      .filter((company) => !isDeleted(company))
      .filter((company) => company.company_name.toLowerCase().includes(search.toLowerCase()));

    if (!sortField) {
      return filtered;
    }

    return [...filtered].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortField) {
        case 'company_name':
          aValue = a.company_name.toLowerCase();
          bValue = b.company_name.toLowerCase();
          break;
        case 'industry':
          aValue = (a.industry ?? "").toLowerCase();
          bValue = (b.industry ?? "").toLowerCase();
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return 0;
    });
  };

  const sortedCompanies = getSortedCompanies();
  const totalPages = total > 0 ? Math.ceil(total / rowsPerPage) : 1;
  const muiPageIndex = Math.min(Math.max(0, page - 1), Math.max(0, totalPages - 1));

  return (
    <div className="border rounded-2 bg-white isPlacementOffice ? my-0: my-2 me-2" style={{ ...(isPlacementOffice ? {height: `calc(100vh - 20px)`} : { height: `calc(100vh - 90px)` }), overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" p={2} style={{ flexShrink: 0 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <TextField
            size="small"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ minWidth: 200 }}
          />
        </Box>
        <button className="btn" onClick={() => { handleModal("new") }}>
          + New Company
        </button>
      </Box>

      <TableContainer component={Paper} className="px-2" style={{ flexGrow: 1, overflow: "auto", borderRadius: 0 }}>
        <Table className="border border-2" size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell style={{ color: "#666" }} className="muifont mui-header-cell">
                S.No
              </TableCell>
              <TableCell
                className="muifont mui-header-cell mui-sortable-header"
                onClick={() => handleSort('company_name')}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <span style={{ color: "#666" }}>Company Name</span>
                  <span style={{ marginLeft: "4px" }}>
                    {sortField === 'company_name' ? (
                      sortDirection === 'asc' ? (
                        <PiSortDescendingLight size={16} />
                      ) : (
                        <PiSortAscendingLight size={16} />
                      )
                    ) : (
                      <HiOutlineDotsVertical size={16} />
                    )}
                  </span>
                </Box>
              </TableCell>
              <TableCell
                className="muifont mui-header-cell mui-sortable-header"
                onClick={() => handleSort('industry')}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <span style={{ color: "#666" }}>Industry</span>
                  <span style={{ marginLeft: "4px" }}>
                    {sortField === 'industry' ? (
                      sortDirection === 'asc' ? (
                        <PiSortDescendingLight size={16} />
                      ) : (
                        <PiSortAscendingLight size={16} />
                      )
                    ) : (
                      <HiOutlineDotsVertical size={16} />
                    )}
                  </span>
                </Box>
              </TableCell>
              <TableCell style={{ color: "#666" }} className="muifont mui-header-cell">
                Ph No
              </TableCell>
              <TableCell style={{ color: "#666" }} className="muifont mui-header-cell">
                Country
              </TableCell>
              <TableCell style={{ color: "#666" }} className="muifont mui-header-cell">
                State
              </TableCell>
              <TableCell style={{ color: "#666" }} className="muifont mui-header-cell">
                City
              </TableCell>
              {isPlacementOffice && (
                <>
                  <TableCell style={{ color: "#666" }} className="muifont mui-header-cell">
                    Open
                  </TableCell>
                  <TableCell style={{ color: "#666" }} className="muifont mui-header-cell">
                    Closed
                  </TableCell>
                  <TableCell style={{ color: "#666" }} className="muifont mui-header-cell">
                    In Progress
                  </TableCell>
                </>
              )}
              <TableCell
                className="muifont mui-header-cell"
                style={{
                  borderRight: "none",
                }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={isPlacementOffice ? 11 : 8} align="center" style={{ padding: "40px" }}>
                  <Spinner animation="border" size="sm" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </TableCell>
              </TableRow>
            ) : sortedCompanies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isPlacementOffice ? 11 : 8} align="center" style={{ padding: "20px", color: "#666" }}>
                  No companies found
                </TableCell>
              </TableRow>
            ) : (
              sortedCompanies.map((company, index) => (
                <TableRow
                  key={index}
                  style={{
                    backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafafa",
                  }}
                  sx={{
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                >
                  <TableCell className="muifont mui-body-cell">
                    {(page - 1) * rowsPerPage + index + 1}
                  </TableCell>
                  <TableCell className="muifont mui-body-cell">
                    {company.company_name || ''}
                  </TableCell>
                  <TableCell className="muifont mui-body-cell">
                    {company.industry ?? '-'}
                  </TableCell>
                  <TableCell className="muifont mui-body-cell">
                    {company.contact_number ?? '-'}
                  </TableCell>
                  <TableCell className="muifont mui-body-cell">
                    {company.country ?? '-'}
                  </TableCell>
                  <TableCell className="muifont mui-body-cell">
                    {company.state ?? '-'}
                  </TableCell>
                  <TableCell className="muifont mui-body-cell">
                    {company.city ?? '-'}
                  </TableCell>
                  {isPlacementOffice && (
                    <>
                      <TableCell className="muifont mui-body-cell">
                        <span
                          role="button"
                          onClick={() => navigate(`/placement-officer/job-profile?company=${company.company_id || ""}&status=open`)}
                          style={{ textDecoration: "underline", cursor: "pointer", color: "#0d6efd" }}
                        >
                          {company.job_profile_open ?? 0}
                        </span>
                      </TableCell>
                      <TableCell className="muifont mui-body-cell">
                        <span
                          role="button"
                          onClick={() => navigate(`/placement-officer/job-profile?company=${company.company_id || ""}&status=closed`)}
                          style={{ textDecoration: "underline", cursor: "pointer", color: "#0d6efd" }}
                        >
                          {company.job_profile_closed ?? 0}
                        </span>
                      </TableCell>
                      <TableCell className="muifont mui-body-cell">
                        <span
                          role="button"
                          onClick={() => navigate(`/placement-officer/job-profile?company=${company.company_id || ""}&status=in_progress`)}
                          style={{ textDecoration: "underline", cursor: "pointer", color: "#0d6efd" }}
                        >
                          {company.job_profile_in_progress ?? 0}
                        </span>
                      </TableCell>
                    </>
                  )}
                  <TableCell
                    className="muifont mui-body-cell"
                    style={{
                      borderRight: "none",
                    }}
                  >
                    {isPlacementOffice && (
                      <>
                        <FaAddressBook
                          className="fs-6 me-2"
                          role="button"
                          title="Contacts"
                          onClick={() => navigate(`/placement-officer/contacts?company=${company.company_id || ""}`)}
                          style={{ cursor: "pointer" }}
                        />
                        <FaBriefcase
                          className="fs-6 me-2"
                          role="button"
                          title="Job Profile"
                          onClick={() => navigate(`/placement-officer/job-profile?company=${company.company_id || ""}`)}
                          style={{ cursor: "pointer" }}
                        />
                      </>
                    )}
                    <FaEdit
                      className="fs-6 me-2"
                      role="button"
                      title="Edit"
                      onClick={() => { handleModal("edit", company) }}
                      style={{ cursor: 'pointer' }}
                    />
                    <FaTrash
                      className="fs-6 me-2"
                      title="Delete"
                      role="button"
                      onClick={() => handleDelete(company)}
                      style={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={muiPageIndex}
        onPageChange={(_, newPage) => {
          setLoading(true);
          setPage(newPage + 1);
        }}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setLoading(true);
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(1);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        disabled={loading}
        sx={{
          flexShrink: 0,
          borderTop: "1px solid #e5e7eb",
          backgroundColor: "#fafafa",
          px: 1,
          "& .MuiTablePagination-toolbar": { minHeight: 44, px: 1.5 },
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
            fontSize: "12px",
            color: "#666",
            mb: 0,
          },
          "& .MuiTablePagination-actions": { ml: 1 },
          "& .MuiIconButton-root": { p: "6px" },
          "& .Mui-disabled": { opacity: 0.45 },
        }}
      />

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{modalType === "new" ? "Add New Company" : "Edit Company"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {error && <p style={{ color: "red" }}>{error}</p>}
            <Form.Group controlId="formCompanyName" className="mt-3 d-flex align-items-center justify-content-between">
              <Form.Label>Company Name</Form.Label>
              <Form.Control
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                placeholder="Enter Company Name"
                style={{
                  width: "50%"
                }}
              />
            </Form.Group>

            <Form.Group controlId="formIndustry" className="mt-3 d-flex align-items-center justify-content-between">
              <Form.Label>Industry</Form.Label>
              <Form.Control
                as="select"
                name="industry"
                value={formData.industry ?? ""}
                onChange={(e) => setFormData((prev) => ({ ...prev, industry: e.target.value }))}
                style={{ width: "50%" }}
              >
                <option value="">Select Industry</option>
                {industries.map((ind, index) => (
                  <option key={index} value={ind}>
                    {ind}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group controlId="formContactNumber" className="mt-3 d-flex align-items-center justify-content-between">
              <Form.Label>Contact Number</Form.Label>
              <Form.Control
                type="text"
                name="contact_number"
                value={formData.contact_number ?? ""}
                onChange={handleChange}
                placeholder="Enter Contact Number"
                style={{ width: "50%" }}
              />
            </Form.Group>

            <Form.Group controlId="formCountry" className="mt-3 d-flex align-items-center justify-content-between">
              <Form.Label>Country</Form.Label>
              <Form.Control
                type="text"
                name="country"
                value={formData.country ?? ""}
                onChange={handleChange}
                placeholder="Enter Country"
                style={{ width: "50%" }}
              />
            </Form.Group>

            <Form.Group controlId="formState" className="mt-3 d-flex align-items-center justify-content-between">
              <Form.Label>State</Form.Label>
              <Form.Control
                type="text"
                name="state"
                value={formData.state ?? ""}
                onChange={handleChange}
                placeholder="Enter State"
                style={{ width: "50%" }}
              />
            </Form.Group>

            <Form.Group controlId="formCity" className="mt-3 d-flex align-items-center justify-content-between">
              <Form.Label>City</Form.Label>
              <Form.Control
                type="text"
                name="city"
                value={formData.city ?? ""}
                onChange={handleChange}
                placeholder="Enter City"
                style={{ width: "50%" }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSave}>
            {loading ? <Spinner animation="border" size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
              <Modal.Header closeButton>
                <Modal.Title>Confirm Delete</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p>Are you sure you want to delete the company: <strong>{selectedCompany?.company_name}</strong>?</p>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                  No
                </Button>
                <Button variant="danger" onClick={confirmDelete}>
                  Yes, Delete
                </Button>
              </Modal.Footer>
            </Modal>
    </div>
  );
};

export default Company;

